// Whether to SHOW demo-seed buttons in the UI. Mirrors the server-side
// can_seed_demo() gate: staff, or a tenant explicitly in demo mode.
// Pages call this and only render their "Demo data" button when true.
import { useEffect, useState } from 'react';
import { supabase } from '@govern360/shared';

export function useCanSeedDemo() {
  const [can, setCan] = useState(false);
  useEffect(() => {
    let active = true;
    supabase.rpc('can_seed_demo').then(({ data }) => { if (active) setCan(!!data); });
    return () => { active = false; };
  }, []);
  return can;
}

// Returns { onboarded, demoMode, toolCount, policyCount } for the current tenant.
export async function getOnboardingStatus() {
  const { data, error } = await supabase.rpc('onboarding_status');
  if (error || !data || !data.length) {
    return { onboarded: true, demoMode: false, toolCount: 0, policyCount: 0, error };
  }
  const row = data[0];
  return {
    onboarded: !!row.onboarded,
    demoMode: !!row.demo_mode,
    toolCount: Number(row.tool_count) || 0,
    policyCount: Number(row.policy_count) || 0,
  };
}

export async function completeOnboarding() {
  const { error } = await supabase.rpc('complete_onboarding');
  return { error };
}

// Whether the wizard should show: not yet onboarded AND no real data yet.
export function shouldShowWizard(status) {
  if (!status) return false;
  return !status.onboarded && status.toolCount === 0 && status.policyCount === 0;
}

// Add a single AI tool during onboarding (minimal fields).
export async function addFirstTool(tenantId, { name, vendor, status }) {
  const { data, error } = await supabase
    .from('ai_tools')
    .insert({ tenant_id: tenantId, name, vendor, status, risk: 'Medium' })
    .select().single();
  return { data, error };
}

// Enable a starter policy during onboarding.
export async function addStarterPolicy(tenantId) {
  // Make sure detectors exist, then add one sensible default policy.
  await supabase.rpc('enable_default_detectors');
  const { data, error } = await supabase
    .from('policies')
    .insert({
      tenant_id: tenantId,
      name: 'Block secrets to any AI',
      scope_type: 'all',
      detector_keys: ['aws_key', 'github_token', 'private_key', 'openai_key'],
      action: 'block',
      priority: 100,
      enabled: true,
    })
    .select().single();
  return { data, error };
}